import React, { useState, useEffect, useRef } from 'react';
import Button from '../UI/Button';
import type { ShelterDog } from '../../types/shelter';
import { getImageUrl } from '../../services/api';

interface AnimalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (animal: Partial<ShelterDog>) => void;
  animal?: ShelterDog | null;
  mode: 'add' | 'edit';
}

const AnimalModal: React.FC<AnimalModalProps> = ({
  isOpen,
  onClose,
  onSave,
  animal,
  mode
}) => {
  const [formData, setFormData] = useState({
    name: '',
    gender: 'male' as 'male' | 'female',
    age: '',
    size: 'mediu' as 'mic' | 'mediu' | 'mare',
    color: '',
    description: '',
    status: 'available' as 'available' | 'adopted' | 'hospital' | 'quarantine' | 'reserved',
    vaccinated: false,
    neutered: false,
    chipped: false,
    entryDate: '',
    adoptionDate: '',
    totalInvested: 0,
    lastVetVisit: '',
    weight: '',
    height: '',
    specialNeeds: '',
    temperament: '',
    goodWithKids: false,
    goodWithCats: false,
    goodWithDogs: false,
    energyLevel: 'medium' as 'low' | 'medium' | 'high',
    trainingLevel: 'basic' as 'none' | 'basic' | 'intermediate' | 'advanced',
    medicalNotes: '',
    behaviorNotes: '',
    adoptionRequirements: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [removeExistingImage, setRemoveExistingImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to convert ISO date to yyyy-MM-dd format
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Extract just the date part
  };

  useEffect(() => {
    if (animal && mode === 'edit') {
      setFormData({
        name: animal.name || '',
        gender: animal.gender || 'male',
        age: animal.age || '',
        size: animal.size || 'mediu',
        color: animal.color || '',
        description: animal.description || '',
        status: animal.status || 'available',
        vaccinated: animal.vaccinated || false,
        neutered: animal.neutered || false,
        chipped: animal.chipped || false,
        entryDate: formatDateForInput(animal.entryDate),
        adoptionDate: formatDateForInput(animal.adoptionDate),
        totalInvested: animal.totalInvested || 0,
        lastVetVisit: formatDateForInput(animal.lastVetVisit),
        weight: animal.weight || '',
        height: animal.height || '',
        specialNeeds: animal.specialNeeds || '',
        temperament: animal.temperament || '',
        goodWithKids: animal.goodWithKids || false,
        goodWithCats: animal.goodWithCats || false,
        goodWithDogs: animal.goodWithDogs || false,
        energyLevel: animal.energyLevel || 'medium',
        trainingLevel: animal.trainingLevel || 'basic',
        medicalNotes: animal.medicalNotes || '',
        behaviorNotes: animal.behaviorNotes || '',
        adoptionRequirements: animal.adoptionRequirements || ''
      });
      
      // Set image preview for existing animal using the animal ID
      if (animal.id) {
        setImagePreview(getImageUrl(animal.id));
      }
      setRemoveExistingImage(false);
      setSelectedImage(null);
      setSearchTerm('');
    } else if (mode === 'add') {
      setFormData({
        name: '',
        gender: 'male',
        age: '',
        size: 'mediu',
        color: '',
        description: '',
        status: 'available',
        vaccinated: false,
        neutered: false,
        chipped: false,
        entryDate: new Date().toISOString().split('T')[0],
        adoptionDate: '',
        totalInvested: 0,
        lastVetVisit: '',
        weight: '',
        height: '',
        specialNeeds: '',
        temperament: '',
        goodWithKids: false,
        goodWithCats: false,
        goodWithDogs: false,
        energyLevel: 'medium',
        trainingLevel: 'basic',
        medicalNotes: '',
        behaviorNotes: '',
        adoptionRequirements: ''
      });
      
      // Reset image preview
      setImagePreview('');
      setSelectedImage(null);
      setRemoveExistingImage(false);
      setSearchTerm('');
    }
  }, [animal, mode, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Vă rugăm să selectați o imagine validă' }));
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Imaginea trebuie să fie mai mică de 5MB' }));
        return;
      }

      setSelectedImage(file);
      setRemoveExistingImage(false);
      setErrors(prev => ({ ...prev, image: '' }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    setRemoveExistingImage(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Numele este obligatoriu';
    }
    if (!formData.age.trim()) {
      newErrors.age = 'Vârsta este obligatorie';
    }
    if (!formData.color.trim()) {
      newErrors.color = 'Culoarea este obligatorie';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Descrierea este obligatorie';
    }
    if (!formData.entryDate) {
      newErrors.entryDate = 'Data intrării este obligatorie';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const animalData: any = {
      ...formData,
      id: animal?.id || undefined, // Don't set ID for new animals
    };

    // Handle image changes
    if (selectedImage) {
      // New image selected
      animalData.imageFile = selectedImage;
    } else if (removeExistingImage && mode === 'edit') {
      // Remove existing image
      animalData.removeImage = true;
    }

    onSave(animalData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'add' ? 'Adaugă animal nou' : 'Editează animal'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații de bază</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nume *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sex
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="male">Mascul</option>
                    <option value="female">Femelă</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vârstă *
                  </label>
                  <select
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.age ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selectați vârsta</option>
                    <option value="0 luni">0 luni</option>
                    <option value="1 lună">1 lună</option>
                    <option value="2 luni">2 luni</option>
                    <option value="3 luni">3 luni</option>
                    <option value="4 luni">4 luni</option>
                    <option value="5 luni">5 luni</option>
                    <option value="6 luni">6 luni</option>
                    <option value="7 luni">7 luni</option>
                    <option value="8 luni">8 luni</option>
                    <option value="9 luni">9 luni</option>
                    <option value="10 luni">10 luni</option>
                    <option value="11 luni">11 luni</option>
                    <option value="1 an">1 an</option>
                    <option value="2 ani">2 ani</option>
                    <option value="3 ani">3 ani</option>
                    <option value="4 ani">4 ani</option>
                    <option value="5 ani">5 ani</option>
                    <option value="6 ani">6 ani</option>
                    <option value="7 ani">7 ani</option>
                    <option value="8 ani">8 ani</option>
                    <option value="9 ani">9 ani</option>
                    <option value="10 ani">10 ani</option>
                    <option value="11 ani">11 ani</option>
                    <option value="12 ani">12 ani</option>
                    <option value="13 ani">13 ani</option>
                    <option value="14 ani">14 ani</option>
                    <option value="15 ani">15 ani</option>
                  </select>
                  {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mărime
                  </label>
                  <select
                    value={formData.size}
                    onChange={(e) => handleInputChange('size', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="mic">Mic</option>
                    <option value="mediu">Mediu</option>
                    <option value="mare">Mare</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Culoare *
                  </label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.color ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.color && <p className="text-red-500 text-xs mt-1">{errors.color}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Greutate (kg)
                  </label>
                  <input
                    type="text"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    placeholder="ex: 25"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Înălțime (cm)
                  </label>
                  <input
                    type="text"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    placeholder="ex: 60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="available">Disponibil</option>
                    <option value="adopted">Adoptat</option>
                    <option value="hospital">La spital</option>
                    <option value="quarantine">În carantină</option>
                    <option value="reserved">Rezervat</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Imagine</h3>
              <div className="flex items-center space-x-4">
                {(imagePreview && !removeExistingImage) && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                      onError={(e) => {
                        // If image fails to load, show placeholder
                        e.currentTarget.src = '/images/default-animal.svg';
                        e.currentTarget.onerror = null; // Prevent infinite loop
                      }}
                    />
                  </div>
                )}
                {removeExistingImage && (
                  <div className="w-32 h-32 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">Nu este imagine</span>
                  </div>
                )}
                <div className="flex flex-col space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    ref={fileInputRef}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {(imagePreview && !removeExistingImage) ? 'Schimbă imagine' : 'Alege imagine'}
                  </button>
                  {(imagePreview && !removeExistingImage) && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Șterge imagine
                    </button>
                  )}
                  {removeExistingImage && (
                    <button
                      type="button"
                      onClick={() => {
                        setRemoveExistingImage(false);
                        if (animal?.id) {
                          setImagePreview(getImageUrl(animal.id));
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      Anulează ștergerea
                    </button>
                  )}
                </div>
              </div>
              {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
              <p className="text-sm text-gray-500 mt-2">
                Formate acceptate: JPG, PNG, WebP. Dimensiune maximă: 5MB.
              </p>
            </div>

            {/* Medical Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații medicale</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="vaccinated"
                    checked={formData.vaccinated}
                    onChange={(e) => handleInputChange('vaccinated', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="vaccinated" className="ml-2 block text-sm text-gray-900">
                    Vaccinat
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="neutered"
                    checked={formData.neutered}
                    onChange={(e) => handleInputChange('neutered', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="neutered" className="ml-2 block text-sm text-gray-900">
                    Sterilizat
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="chipped"
                    checked={formData.chipped}
                    onChange={(e) => handleInputChange('chipped', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="chipped" className="ml-2 block text-sm text-gray-900">
                    Microcipat
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ultima vizită veterinar
                  </label>
                  <input
                    type="date"
                    value={formData.lastVetVisit}
                    onChange={(e) => handleInputChange('lastVetVisit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nevoi speciale
                  </label>
                  <input
                    type="text"
                    value={formData.specialNeeds}
                    onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
                    placeholder="ex: medicație zilnică, dietă specială"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notițe medicale
                </label>
                <textarea
                  value={formData.medicalNotes}
                  onChange={(e) => handleInputChange('medicalNotes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Behavior & Temperament */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Comportament și temperament</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nivel de energie
                  </label>
                  <select
                    value={formData.energyLevel}
                    onChange={(e) => handleInputChange('energyLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="low">Scăzut</option>
                    <option value="medium">Mediu</option>
                    <option value="high">Ridicat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nivel de dresaj
                  </label>
                  <select
                    value={formData.trainingLevel}
                    onChange={(e) => handleInputChange('trainingLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="none">Fără dresaj</option>
                    <option value="basic">De bază</option>
                    <option value="intermediate">Intermediar</option>
                    <option value="advanced">Avansat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperament
                  </label>
                  <input
                    type="text"
                    value={formData.temperament}
                    onChange={(e) => handleInputChange('temperament', e.target.value)}
                    placeholder="ex: prietenos, calm, jucăuș"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="goodWithKids"
                    checked={formData.goodWithKids}
                    onChange={(e) => handleInputChange('goodWithKids', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="goodWithKids" className="ml-2 block text-sm text-gray-900">
                    Bun cu copiii
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="goodWithCats"
                    checked={formData.goodWithCats}
                    onChange={(e) => handleInputChange('goodWithCats', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="goodWithCats" className="ml-2 block text-sm text-gray-900">
                    Bun cu pisicile
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="goodWithDogs"
                    checked={formData.goodWithDogs}
                    onChange={(e) => handleInputChange('goodWithDogs', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="goodWithDogs" className="ml-2 block text-sm text-gray-900">
                    Bun cu alți câini
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notițe comportamentale
                </label>
                <textarea
                  value={formData.behaviorNotes}
                  onChange={(e) => handleInputChange('behaviorNotes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Administrative Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații administrative</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data intrării *
                  </label>
                  <input
                    type="date"
                    value={formData.entryDate}
                    onChange={(e) => handleInputChange('entryDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.entryDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.entryDate && <p className="text-red-500 text-xs mt-1">{errors.entryDate}</p>}
                </div>

                {formData.status === 'adopted' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data adopției
                    </label>
                    <input
                      type="date"
                      value={formData.adoptionDate}
                      onChange={(e) => handleInputChange('adoptionDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total investit (RON)
                  </label>
                  <input
                    type="number"
                    value={formData.totalInvested}
                    onChange={(e) => handleInputChange('totalInvested', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descriere *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Descriere detaliată a animalului..."
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Adoption Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cerințe pentru adopție
              </label>
              <textarea
                value={formData.adoptionRequirements}
                onChange={(e) => handleInputChange('adoptionRequirements', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="ex: casă cu curte, experiență cu câini mari, fără copii mici..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button variant="outline" onClick={onClose}>
                Anulează
              </Button>
              <Button variant="primary" type="submit">
                {mode === 'add' ? 'Adaugă animal' : 'Salvează modificările'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AnimalModal; 